import { useState } from 'react';
import snakeCaseKeys from 'snakecase-keys';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLocation } from '@xing-com/crate-core-router';
import { useFetch, useLoginAppUrl } from '@xing-com/crate-xinglet';

import { getCSRFToken } from './get-csrf-token';

export function useTfaSubmit(): {
  loading: boolean;
  error: string;
  success: boolean;
  execute: (code: string, whitelistBrowser: boolean) => Promise<void>;
} {
  const { track } = useTracking<{
    PropTrackAction: string;
  }>();

  const host = useLoginAppUrl().replace(/\/$/, '');
  const fetch = useFetch();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const execute = async (
    code: string,
    whitelistBrowser: boolean
  ): Promise<void> => {
    setLoading(true);

    const urlParams = new URLSearchParams(location.search);
    const queryParams = Object.fromEntries(urlParams.entries());

    const payload = snakeCaseKeys({
      tfaCode: code,
      whitelistBrowser: whitelistBrowser,
      ...queryParams,
    });

    const data = await fetch(host + '/login/api/tfa', {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json',
        'x-csrf-token': getCSRFToken(),
      },
      body: JSON.stringify(payload),
      method: 'POST',
    });

    const parsedData = await data.json();

    if (parsedData.success) {
      setSuccess(true);

      if (parsedData.auth_token) {
        const dataPong = await fetch(`${parsedData.target}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            Accept: 'application/json',
            'x-csrf-token': getCSRFToken(),
          },
          mode: 'cors',
          credentials: 'include',
          body: JSON.stringify({
            auth_token: parsedData.auth_token,
            logged_out_sid: parsedData.logged_out_sid,
          }),
        });
        const parsedDataPong = await dataPong.json();

        if (parsedDataPong.success) {
          if (typeof window !== 'undefined') {
            window.location.href = parsedDataPong.target;
          }
        }
      } else {
        if (typeof window !== 'undefined') {
          window.location.href = parsedData.target;
        }
      }
    } else {
      track({
        type: 'interaction',
        event: 'PropTrackAction',
        PropTrackAction: 'welcome_login_tfa_error_code',
      });

      if (parsedData.error === 'LOGIN_REQUIRED') {
        window.location.href = `${host}?error=LOGIN_REQUIRED`;
      } else {
        setSuccess(false);
        setError(parsedData.description);
      }
    }

    setLoading(false);
  };

  return { loading, error, success, execute };
}
